import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import "./Earings.css";

function AdminEarnings() {
  const beckendLocalApiUrl = process.env.REACT_APP_BACKEND_LOCAL_API;
  const beckendLiveApiUrl = process.env.REACT_APP_BACKEND_LIVE_API;
  const nodeMode = process.env.NODE_ENV;
  const baseUrl =
    nodeMode === "development" ? beckendLocalApiUrl : beckendLiveApiUrl;

  const [limit, setLimit] = useState(15);
  const [pageNumber, setPageNumber] = useState(1);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({
    totalAllTime: 0,
    today: 0,
    week: 0,
    month: 0,
  });
  const [monthlyChart, setMonthlyChart] = useState([]);
  const [chartDays, setChartDays] = useState(30);
  const [topUsers, setTopUsers] = useState([]);
  const [fromDate, setFromDate] = useState(
    new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10)
  );
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  const headers = useCallback(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }),
    []
  );

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError("");
      try {
        const h = headers();
        const [listRes, summaryRes, chartRes, topRes] = await Promise.all([
          axios.get(
            `${baseUrl}admin/earning?page=${pageNumber}&_limit=${limit}&FROM_DATE=${fromDate}&TO_DATE=${toDate}`,
            { headers: h }
          ),
          axios.get(`${baseUrl}admin/earning/summary`, { headers: h }),
          axios.get(`${baseUrl}admin/earning/chart?days=${chartDays}`, {
            headers: h,
          }),
          axios.get(`${baseUrl}admin/earning/top-users?days=30&limit=12`, {
            headers: h,
          }),
        ]);

        setEarnings(listRes.data.admin || []);
        setNumberOfPages(listRes.data.totalPages || 0);
        setTotalRows(listRes.data.total || 0);
        setSummary(summaryRes.data || {});
        setMonthlyChart(chartRes.data.chart || []);
        setTopUsers(topRes.data.topUsers || []);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.response?.data ||
            err?.message ||
            "Failed to fetch earnings"
        );
      }
      setLoading(false);
    };
    fetchAll();
  }, [pageNumber, limit, fromDate, toDate, baseUrl, headers, chartDays]);

  const chartData = useMemo(() => {
    const labels = (monthlyChart || []).map((c) => c._id);
    const data = (monthlyChart || []).map((c) =>
      Number(Number(c.total).toFixed(2))
    );
    return {
      labels,
      datasets: [
        {
          label: "Admin earning (₹)",
          data,
          backgroundColor: "rgba(59, 130, 246, 0.45)",
          borderColor: "rgba(37, 99, 235, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [monthlyChart]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: true, position: "top" },
      scales: {
        yAxes: [
          {
            ticks: { beginAtZero: true },
            gridLines: { color: "rgba(148, 163, 184, 0.25)" },
          },
        ],
        xAxes: [
          {
            gridLines: { display: false },
            ticks: { maxRotation: 45, minRotation: 0 },
          },
        ],
      },
    }),
    []
  );

  const pages = [];
  for (let i = 1; i <= numberOfPages; i++) pages.push(i);

  const uid = (row) =>
    row.userId && typeof row.userId === "object"
      ? row.userId._id
      : row.userId;

  return (
    <div className="admin-content lp-admin-earnings">
      <div className="lp-earn-hero">
        <h4 className="mb-1" style={{ fontWeight: 700 }}>
          Admin earnings
        </h4>
       
      </div>

      <div className="row">
        {[
          { key: "total", label: "All-time total", value: summary.totalAllTime },
          { key: "today", label: "Today", value: summary.today },
          { key: "week", label: "This week", value: summary.week },
          { key: "month", label: "This month", value: summary.month },
        ].map((s) => (
          <div key={s.key} className="col-md-3 col-sm-6 mb-3">
            <div className="lp-earn-stat">
              <div className="label">{s.label}</div>
              <div className="value">₹{Number(s.value || 0).toFixed(2)}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        <div className="col-lg-8 mb-3">
          <div className="lp-earn-card">
            <div className="card-header-lp d-flex flex-wrap justify-content-between align-items-center">
              <span>Earning trend</span>
              <select
                className="form-control form-control-sm"
                style={{ width: "auto" }}
                value={chartDays}
                onChange={(e) => setChartDays(Number(e.target.value))}
              >
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            <div className="lp-earn-chart-wrap">
              {monthlyChart.length === 0 ? (
                <div className="text-center text-muted pt-5">
                  No chart data in this range
                </div>
              ) : (
                <Bar data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-4 mb-3">
          <div className="lp-earn-card h-100">
            <div className="card-header-lp">Top sources (30 days)</div>
            <div className="p-3">
              {topUsers.length === 0 ? (
                <div className="text-muted small">No data</div>
              ) : (
                topUsers.map((u, idx) => (
                  <div key={String(u._id)} className="lp-top-user">
                    <span className="rank">#{idx + 1}</span>
                    <div className="flex-grow-1 px-2">
                      {u._id ? (
                        <Link
                          className="lp-user-link"
                          to={`/user/view_user/${u._id}`}
                        >
                          {u.Name || "User"}
                        </Link>
                      ) : (
                        <span>—</span>
                      )}
                      <div className="small text-muted">
                        {u.entries || 0} entries
                      </div>
                    </div>
                    <strong>₹{Number(u.totalAmount || 0).toFixed(2)}</strong>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="lp-earn-card">
            <div className="card-header-lp">Earnings log</div>
            <div className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <div className="d-flex align-items-center flex-wrap mb-2 mb-md-0">
                  <label className="mr-2 mb-0 small text-muted">From</label>
                  <input
                    type="date"
                    className="form-control form-control-sm mr-3"
                    style={{ width: "auto" }}
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setPageNumber(1);
                    }}
                  />
                  <label className="mr-2 mb-0 small text-muted">To</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ width: "auto" }}
                    value={toDate}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setPageNumber(1);
                    }}
                  />
                </div>
                <div className="d-flex align-items-center">
                  <label className="mr-2 mb-0 small text-muted">Rows</label>
                  <select
                    className="form-control form-control-sm"
                    style={{ width: "auto" }}
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPageNumber(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {loading && (
                <div className="text-center text-muted py-3">Loading…</div>
              )}
              {error && (
                <div className="alert alert-danger">
                  {String(error)}
                </div>
              )}

              <div className="small text-muted mb-2">
                Showing {earnings.length} of {totalRows} in date range
              </div>

              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>From user (lost)</th>
                      <th>Phone</th>
                      <th>Amount</th>
                      <th>Game / stake</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {earnings.length === 0 && !loading ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          No earnings in this range
                        </td>
                      </tr>
                    ) : (
                      earnings.map((row, idx) => {
                        const id = uid(row);
                        const phone = row.userPhone;
                        return (
                          <tr key={row._id}>
                            <td>{(pageNumber - 1) * limit + idx + 1}</td>
                            <td>
                              {id ? (
                                <Link
                                  className="lp-user-link"
                                  to={`/user/view_user/${id}`}
                                >
                                  {row.User_Name || "—"}
                                </Link>
                              ) : (
                                row.User_Name || "—"
                              )}
                            </td>
                            <td>{phone != null ? phone : "—"}</td>
                            <td>
                              <strong>
                                ₹{Number(row.Ammount ?? row.amount ?? 0).toFixed(2)}
                              </strong>
                            </td>
                            <td>
                              <div>{row.Game_Title || "—"}</div>
                              {row.stakeAmount != null && (
                                <div className="small text-muted">
                                  Stake ₹{row.stakeAmount}
                                </div>
                              )}
                            </td>
                            <td className="small">
                              {row.createdAt
                                ? new Date(row.createdAt).toLocaleString()
                                : "—"}
                            </td>
                            <td>
                              {id ? (
                                <Link
                                  className="btn btn-sm btn-outline-primary"
                                  to={`/user/view_user/${id}`}
                                >
                                  Profile
                                </Link>
                              ) : null}
                              {row.challengeId ? (
                                <Link
                                  className="btn btn-sm btn-outline-secondary ml-1"
                                  to={`/view/${row.challengeId}`}
                                >
                                  Challenge
                                </Link>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {numberOfPages > 1 && (
                <div className="d-flex justify-content-center flex-wrap mt-3">
                  {pages.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm ${
                        pageNumber === p ? "btn-primary" : "btn-outline-primary"
                      } mx-1 mb-1`}
                      onClick={() => setPageNumber(p)}
                      disabled={loading}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminEarnings;
